import React, { useState } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock,
  Mail,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  UserPlus,
  LogIn,
  AlertTriangle,
  Clock,
  XCircle,
} from "lucide-react";

export default function AdminLogin() {
  const navigate = useNavigate();

  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  // status: error | pending | denied | success
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");

  /* ================= SUBMIT ================= */
  const submit = async () => {
    setStatus(null);
    setMessage("");

    if (!email || !password) {
      setStatus("error");
      setMessage("Email and password are required.");
      return;
    }

    if (mode === "register" && password.length < 6) {
      setStatus("error");
      setMessage("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);

    try {
      /* ================= REGISTER ================= */
      if (mode === "register") {
        const cred = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );

        await setDoc(doc(db, "users", cred.user.uid), {
          email,
          role: "admin",
          approved: false,
          createdAt: serverTimestamp(),
        });

        setStatus("pending");
        setMessage(
          "Your admin account request has been submitted. Please wait for approval."
        );
        setMode("login");
        return;
      }

      /* ================= LOGIN ================= */
      const cred = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      const snap = await getDoc(doc(db, "users", cred.user.uid));

      if (!snap.exists()) {
        setStatus("denied");
        setMessage(
          "You do not have administrative access. Contact system administrator."
        );
        return;
      }

      if (!snap.data().approved) {
        setStatus("pending");
        setMessage(
          "Your admin account is awaiting approval. You will be notified once approved."
        );
        return;
      }

      setStatus("success");
      setMessage("Access granted. Redirecting to dashboard…");

      setTimeout(() => {
        navigate("/admin/dashboard");
      }, 900);
    } catch {
      setStatus("error");
      setMessage("Invalid login credentials.");
    } finally {
      setLoading(false);
    }
  };

  /* ================= STATUS UI ================= */
  const StatusBox = () => {
    if (!status) return null;

    const styles = {
      error: {
        bg: "bg-red-500/10 text-red-600",
        icon: XCircle,
      },
      pending: {
        bg: "bg-yellow-500/10 text-yellow-700",
        icon: Clock,
      },
      denied: {
        bg: "bg-orange-500/10 text-orange-700",
        icon: AlertTriangle,
      },
      success: {
        bg: "bg-green-500/10 text-green-600",
        icon: ShieldCheck,
      },
    };

    const Icon = styles[status].icon;

    return (
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        className={`mb-5 flex items-start gap-3 p-4 rounded-xl text-sm ${styles[status].bg}`}
      >
        <Icon size={20} />
        <span>{message}</span>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black px-4">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8"
      >
        {/* HEADER */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 flex items-center justify-center h-14 w-14 rounded-full bg-sky-100">
            <ShieldCheck className="text-sky-600" size={28} />
          </div>

          <h2 className="text-2xl font-bold mb-1">
            {mode === "login" ? "Admin Access" : "Request Admin Access"}
          </h2>

          <p className="text-sm text-gray-500">
            Secure administrative authentication
          </p>
        </div>

        {/* STATUS MESSAGE */}
        <AnimatePresence>
          <StatusBox />
        </AnimatePresence>

        {/* EMAIL */}
        <Field
          icon={Mail}
          label="Email Address"
          type="email"
          value={email}
          onChange={setEmail}
        />

        {/* PASSWORD */}
        <div className="mb-6">
          <label className="text-sm font-medium mb-1 block text-gray-700">
            Password
          </label>

          <div className="relative">
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
              size={18}
            />

            <input
              type={showPass ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-12 py-3 rounded-xl border border-gray-400 focus:ring-2 focus:ring-sky-200"
            />

            <button
              type="button"
              onClick={() => setShowPass(!showPass)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
            >
              {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* SUBMIT */}
        <button
          onClick={submit}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-4 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-semibold disabled:opacity-60"
        >
          {loading && <Loader2 className="animate-spin" size={18} />}
          {!loading &&
            (mode === "login" ? <LogIn size={18} /> : <UserPlus size={18} />)}
          {mode === "login" ? "Login" : "Request Access"}
        </button>

        {/* SWITCH MODE */}
        <div className="mt-6 text-center text-sm">
          {mode === "login" ? (
            <button
              onClick={() => setMode("register")}
              className="text-sky-600 hover:underline"
            >
              Request admin access
            </button>
          ) : (
            <button
              onClick={() => setMode("login")}
              className="text-sky-600 hover:underline"
            >
              Back to login
            </button>
          )}
        </div>

        <p className="mt-6 text-xs text-center text-gray-400">
          Authorized personnel only
        </p>
      </motion.div>
    </div>
  );
}

/* ================= FIELD ================= */
function Field({ icon: Icon, label, type, value, onChange }) {
  return (
    <div className="mb-5">
      <label className="text-sm font-medium mb-1 block text-gray-700">
        {label}
      </label>
      <div className="relative">
        <Icon
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
          size={18}
        />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-400"
        />
      </div>
    </div>
  );
}
